import { Box, Checkbox, Chip, FormControlLabel, Stack } from '@mui/material';
import {
  DataGridPremium,
  ExportExcel,
  FilterPanelTrigger,
  GridColDef,
  QuickFilter,
  QuickFilterControl,
  Toolbar,
} from '@mui/x-data-grid-premium';
import { useCallback, useContext, useEffect, useState } from 'react';

import { Spinner } from '@/components/common/Spinner';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { UsersContext } from '@/Contexts/UsersContext';
import { UserProps, UserType, userTypeAsString } from '@/models/User';
import { dataGridStringCompare, GridLocaleTextOverride } from '@/utils/dataGridUtil';
import UserDataGridDetails from './UserDataGridDetails';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { stringHasValue } from '@/utils/stringUtil';

function ManageUsers({ currentReviewerStateIds }: { currentReviewerStateIds: string[] | null }) {
  const { partnerGroupId } = useContext(SelectedPartnerContext);
  const { locale } = useContext(LocaleContext);

  const { userType, usersAll } = useContext(UsersContext);

  const [applicants, setApplicants] = useState<UserProps[] | null>(null);
  const [hideInactiveUsers, setHideInactiveUsers] = useState(true);

  const GridToolBarCustom = useCallback(() => {
    return (
      <Toolbar>
        <FilterPanelTrigger size='small'>Filters</FilterPanelTrigger>
        <ExportExcel size='small'>Export</ExportExcel>
        <FormControlLabel
          label='Hide Inactive'
          control={
            <Checkbox
              size='small'
              checked={hideInactiveUsers}
              onChange={(event) => setHideInactiveUsers(event.target.checked)}
            />
          }
          sx={{ ml: 1 }}
        />
        <Box sx={{ flex: 1 }}></Box>
        <QuickFilter expanded debounceMs={1500}>
          <QuickFilterControl
            size='small'
            placeholder='Search'
            style={{ minWidth: 260 }}
            slotProps={{ input: { style: { borderRadius: '14px' } } }}
          />
        </QuickFilter>
      </Toolbar>
    );
  }, [hideInactiveUsers]);

  useEffect(() => {
    if (usersAll != null) {
      if (userType == UserType.Applicant) {
        const ars = usersAll.filter(
          (x: UserProps) =>
            x.userType == UserType.Applicant &&
            x.partnerGroup?.id != null &&
            x.partnerGroup.id == partnerGroupId
        );
        setApplicants(ars);
      } else if (userType == UserType.Adf) {
        setApplicants(usersAll);
      } else if (userType == UserType.StateCoordinator && currentReviewerStateIds != null) {
        const rrs = usersAll.filter(
          (x: UserProps) =>
            x.contact?.stateId != null &&
            x.userType == UserType.RegionalCoordinator &&
            currentReviewerStateIds.includes(x.contact.stateId)
        );

        const ars = usersAll.filter((x: UserProps) => rrs.map((r) => r.email).includes(x.email));
        setApplicants(ars);
      }
    }
  }, [userType, usersAll, partnerGroupId, currentReviewerStateIds]);

  let applicantCols: GridColDef[] = [];

  if (userType == UserType.StateCoordinator) {
    applicantCols = [
      {
        field: 'email',
        headerName: 'Email',
        width: 300,
        sortComparator: (v1: string, v2: string) => dataGridStringCompare(v1, v2),
      },
      {
        field: 'firstName',
        headerName: 'First Name',
        width: 200,
        valueGetter: (_, row: UserProps) => {
          const user: UserProps = row;

          return user?.contact?.firstName;
        },
        renderCell: (params) => {
          const user: UserProps = params?.row as UserProps;

          if (!stringHasValue(user?.contact?.firstName)) return <></>;

          return user.contact.firstName;
        },
        sortComparator: (v1: string, v2: string) => dataGridStringCompare(v1, v2),
      },
      {
        field: 'lastName',
        headerName: 'Last Name',
        width: 200,
        valueGetter: (_, row: UserProps) => {
          const user: UserProps = row;

          return user?.contact?.lastName;
        },
        renderCell: (params) => {
          const user: UserProps = params?.row as UserProps;

          if (!stringHasValue(user?.contact?.lastName)) return <></>;

          return user.contact.lastName;
        },
        sortComparator: (v1: string, v2: string) => dataGridStringCompare(v1, v2),
      },
    ];
  } else {
    applicantCols = [
      {
        field: 'disabled',
        headerName: 'Disabled',
      },
      {
        field: 'userType',
        headerName: 'Flag',
        flex: 1,
        valueGetter: (_, row: UserProps) => {
          const user: UserProps = row;
          return userTypeAsString(user.userType);
        },
        renderCell: (params) => {
          const getChip = (user: UserProps) => {
            if (user.validated || user.userType != UserType.Applicant) {
              const impersonating =
                user.userCanImpersonate == true && stringHasValue(user.userImpersonated);
              const currentUserType: UserType | null | undefined = impersonating
                ? user.userAccountType
                : user.userType;

              if (currentUserType == null) return <></>;
              if (user.disabled === true) {
                return (
                  <Chip label={`${userTypeAsString(currentUserType)} (Disabled)`} color='error' />
                );
              }

              if (impersonating)
                return (
                  <Chip
                    label={`${userTypeAsString(currentUserType)} (Impersonating)`}
                    color='warning'
                  />
                );

              return <Chip color='primary' label={userTypeAsString(currentUserType)} />;
            } else {
              return (
                <Chip label={`${userTypeAsString(user.userType)} Pending Approval`} color='error' />
              );
            }
          };

          const user: UserProps = params.row as UserProps;
          const chip = getChip(user);
          return (
            <Box height='100%' sx={{ display: 'flex', alignItems: 'center' }}>
              {chip}
            </Box>
          );
        },
        sortComparator: (v1: string, v2: string) => dataGridStringCompare(v1, v2),
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        sortComparator: (v1: string, v2: string) => dataGridStringCompare(v1, v2),
        renderCell: (params) => {
          const user: UserProps = params?.row as UserProps;

          if (!stringHasValue(user?.email)) return <></>;

          if (user.userEmail != null) {
            return user.userEmail + ' (as ' + user.email + ')';
          }

          return user.email;
        },
      },
      {
        field: 'firstName',
        headerName: 'First Name',
        flex: 1,
        valueGetter: (_, row: UserProps) => {
          const user: UserProps = row;

          return user?.contact?.firstName;
        },
        renderCell: (params) => {
          const user: UserProps = params?.row as UserProps;

          if (!stringHasValue(user?.contact?.firstName)) return <></>;

          return user.contact.firstName;
        },
        sortComparator: (v1: string, v2: string) => dataGridStringCompare(v1, v2),
      },
      {
        field: 'lastName',
        headerName: 'Last Name',
        flex: 1,
        valueGetter: (_, row: UserProps) => {
          const user: UserProps = row;

          return user?.contact?.lastName;
        },
        renderCell: (params) => {
          const user: UserProps = params?.row as UserProps;

          if (!stringHasValue(user?.contact?.lastName)) return <></>;

          return user.contact.lastName;
        },
        sortComparator: (v1: string, v2: string) => dataGridStringCompare(v1, v2),
      },
      {
        field: 'title',
        headerName: 'Professional Title',
        flex: 1,
        valueGetter: (_, row: UserProps) => {
          const user: UserProps = row;

          return user?.contact?.title;
        },
        renderCell: (params) => {
          const user: UserProps = params?.row as UserProps;

          if (!stringHasValue(user?.contact?.title)) return <></>;

          return user.contact.title;
        },
        sortComparator: (v1: string, v2: string) => dataGridStringCompare(v1, v2),
      },
      {
        field: 'partnerGroupName',
        headerName: 'Partner Group',
        flex: 1,
        valueGetter: (_, row: UserProps) => {
          const user: UserProps = row;

          return user?.partnerGroup?.partnerName;
        },
        renderCell: (params) => {
          const user = params.row as UserProps;
          if (user.userType != UserType.Applicant || user.partnerGroup == null) {
            return <></>;
          }
          return user.partnerGroup.partnerName;
        },
        sortComparator: (v1: string, v2: string) => dataGridStringCompare(v1, v2),
      },
    ];
  }

  const datagridSx = {
    marginLeft: '10px',
    marginRight: '10px',
    flex: '1 1 auto',
  };

  const handleRowUpdate = useCallback(
    (usersToUpdate: UserProps[]) => {
      if (applicants == null) return;

      const updatedApplicants = applicants.map((user) => {
        if (usersToUpdate.map((u) => u.b2CId).includes(user.b2CId)) {
          return usersToUpdate.find((u) => u.b2CId == user.b2CId)!;
        }
        return user;
      });

      setApplicants(updatedApplicants);
    },
    [applicants]
  );

  const visibleRows = (applicants ?? [])
    .filter((x: UserProps) => stringHasValue(x.b2CId))
    .filter((x: UserProps) =>
      hideInactiveUsers ? x.disabled === false && x.contactId != null : true
    );

  return (
    <Box width='100%' height='70vh' flexGrow={1}>
      <DataGridPremium
        showToolbar
        localeText={GridLocaleTextOverride(locale)}
        getRowId={(row: UserProps) => row.b2CId}
        initialState={{
          density: 'compact',
          sorting: { sortModel: [{ field: 'disabled', sort: 'asc' }] },
          columns: { columnVisibilityModel: { disabled: false } },
        }}
        getDetailPanelContent={(params) => {
          if (params.row.b2CId == null) return <></>;

          return params != null ? (
            <UserDataGridDetails userRow={params} updateRow={handleRowUpdate} />
          ) : (
            <> </>
          );
        }}
        getDetailPanelHeight={() => 'auto'}
        disableVirtualization
        disableDensitySelector
        sx={datagridSx}
        rows={visibleRows}
        columns={applicantCols}
        autoPageSize
        pagination
        onRowClick={() => {}}
        slots={{
          noRowsOverlay: () => {
            return (
              <Stack height='100%' alignItems='center' justifyContent='center'>
                {applicants == null && (
                  <div>
                    <div
                      style={{
                        width: 'auto',
                        display: 'inline-block',
                      }}
                    >
                      {'Loading...'}
                    </div>
                    <div
                      style={{
                        display: 'inline-block',
                        position: 'relative',
                        top: '3px',
                        width: '20px',
                        height: '20px',
                        marginLeft: '10px',
                      }}
                    >
                      <Spinner type='circular' size={16} />
                    </div>
                  </div>
                )}
                {applicants != null && applicants.length == 0 && <div>No Users</div>}
              </Stack>
            );
          },
          noResultsOverlay: () => (
            <Stack height='100%' alignItems='center' justifyContent='center'>
              <div>No Users</div>
            </Stack>
          ),
          toolbar: GridToolBarCustom,
        }}
      />
    </Box>
  );
}

export default ManageUsers;
